import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X, Filter, Zap, User, Layers, Package } from "lucide-react";

const SRC_LABEL = { manual: "Manuale", scheduled: "Programmazione automatica", bulk: "Azione massiva", import: "Import", undo: "Ripristino" };
const SRC_COLOR = { manual: "bg-sand-100 border-sand-300", scheduled: "bg-blue-50 border-blue-300 text-blue-800",
                    bulk: "bg-amber-50 border-amber-300 text-amber-800", undo: "bg-purple-50 border-purple-300 text-purple-800",
                    import: "bg-sand-100 border-sand-300" };

function fmtDT(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtHM(iso) {
  try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function AdminActivity() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [srcFilter, setSrcFilter] = useState("all");
  const [users, setUsers] = useState([]);

  const load = async () => {
    const params = new URLSearchParams({ limit: "300" });
    if (dateFilter) params.set("date", dateFilter);
    if (userFilter) params.set("user_email", userFilter);
    if (srcFilter && srcFilter !== "all") params.set("source", srcFilter);
    const r = await api.get(`/admin/activity?${params.toString()}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [dateFilter, userFilter, srcFilter]);
  useEffect(() => {
    // pull list of unique users from data for the filter dropdown
    const set = new Map();
    items.forEach((i) => { if (i.user_email) set.set(i.user_email, i.user_name); });
    setUsers([...set.entries()]);
  }, [items]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return items.filter((i) =>
      (i.entity_name || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(s) ||
      (i.user_name || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(s)
    );
  }, [items, q]);

  // Group by date (YYYY-MM-DD) local
  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((it) => {
      const d = new Date(it.at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      (g[key] = g[key] || []).push(it);
    });
    return g;
  }, [filtered]);

  return (
    <div data-testid="admin-activity-page">
      <div className="eyebrow">Amministrazione</div>
      <h1 className="text-4xl mt-1">Cronologia disponibilità</h1>
      <p className="text-sm text-ink-500 mt-1">Timeline unificata di tutti i cambi di disponibilità (prodotti e categorie).</p>

      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none" />
          <Input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Cerca prodotto o utente…" className="pl-9 pr-9 rounded-full" />
          {q && (
            <button data-testid="activity-search-clear" onClick={() => setQ("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-sand-200 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Input type="date" data-testid="activity-date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="rounded-full" />
        <Select value={userFilter || "all"} onValueChange={(v) => setUserFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="activity-user"><SelectValue placeholder="Tutti gli utenti" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli utenti</SelectItem>
            {users.map(([email, name]) => <SelectItem key={email} value={email}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={srcFilter} onValueChange={setSrcFilter}>
          <SelectTrigger data-testid="activity-source"><SelectValue placeholder="Origine" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le origini</SelectItem>
            {Object.entries(SRC_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 && (
        <div className="card-elevated p-8 mt-6 text-center text-ink-500">Nessuna attività trovata con i filtri correnti.</div>
      )}

      <div className="mt-6 space-y-6">
        {Object.entries(groups).map(([date, entries]) => (
          <section key={date} data-testid={`activity-day-${date}`}>
            <div className="font-serif text-xl mb-2">{new Date(date + "T00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</div>
            <ol className="border-l-2 border-sand-300 pl-5 space-y-2">
              {entries.map((e) => (
                <li key={e.id} className="relative" data-testid={`activity-entry-${e.id}`}>
                  <div className={`absolute -left-[26px] top-1.5 w-3 h-3 rounded-full ${e.to_state ? "bg-emerald-500" : "bg-red-500"}`} />
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-ink-500 font-mono w-12">{fmtHM(e.at)}</span>
                    <span className="chip text-[11px] flex items-center gap-1">
                      {e.entity_type === "product" ? <Package className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
                      {e.entity_type === "product" ? "Prodotto" : "Categoria"}
                    </span>
                    <b className="text-sm">{e.entity_name}</b>
                    <span className={e.from_state ? "text-emerald-700 text-sm" : "text-red-700 text-sm"}>{e.from_state ? "Disponibile" : "Esaurito"}</span>
                    <span className="text-ink-500 text-xs">→</span>
                    <span className={e.to_state ? "text-emerald-700 text-sm font-medium" : "text-red-700 text-sm font-medium"}>{e.to_state ? "Disponibile" : "Esaurito"}</span>
                    <span className="text-xs text-ink-500 ml-auto flex items-center gap-1"><User className="w-3 h-3" />{e.user_name}</span>
                    <span className={`chip text-[10px] ${SRC_COLOR[e.source] || "bg-sand-100 border-sand-300"}`}>{SRC_LABEL[e.source] || e.source}</span>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}
